import { supabase } from '@/lib/supabase'
import { normalizeAuthorName } from '@/shared/utils/authorName'
import {
  fetchVisionPostAttachmentsByVisionIds,
  type VisionPostAttachment,
} from './visionPostAttachmentsService'
import { normalizeLinkedOpportunityId } from '../utils/normalizeLinkedOpportunityId'
import { normalizeLinkedTimelineRootId } from '../utils/normalizeLinkedTimelineRootId'

export type VisionItem = {
  id: number
  authorId: string
  authorName: string
  authorAvatarUrl: string | null
  title: string
  contentHtml: string
  attachments: VisionPostAttachment[]
  linkedOpportunityId: number | null
  linkedTimelineRootId: number | null
  createdAt: string
  canView: boolean
  requiredPlan: VisionRequiredPlan | null
  peopleCount: number | null
}

export type VisionRequiredPlan = {
  id: string
  name: string
  amount_cents: number | null
  currency: string | null
  interval: string | null
}

export type VisionTimelineRootCandidate = {
  id: number
  title: string
  contentHtml: string
  authorName: string
  authorAvatarUrl: string | null
  createdAt: string
}

export async function fetchVisionEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_vision_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_vision_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_vision_enabled error', e)
    return false
  }
}

export async function fetchVisionPostEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_vision_post_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_vision_post_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_vision_post_enabled error', e)
    return false
  }
}

export async function createVision(params: {
  title: string
  html: string
  linkedOpportunityId?: number | null
  linkedTimelineRootId?: number | null
}): Promise<VisionItem | null> {
  const title = String(params.title || '').trim()
  const html = String(params.html || '').trim()
  if (!title || !html) return null

  const linkedOpportunityId = normalizeLinkedOpportunityId(params.linkedOpportunityId)
  const linkedTimelineRootId = normalizeLinkedTimelineRootId(params.linkedTimelineRootId)
  const effectiveTimelineRootId = linkedOpportunityId ? null : linkedTimelineRootId

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuario nao autenticado')

  const meta = (user?.user_metadata || {}) as any

  let profileName: string | null = null
  let profileAvatarUrl: string | null | undefined

  try {
    const { data: profile, error: profileError } = await supabase
      .from('profiles' as any)
      .select('full_name,avatar_url')
      .eq('id', userId)
      .maybeSingle()

    if (!profileError && profile) {
      profileName = (profile as any).full_name ?? null
      if (
        typeof (profile as any).avatar_url === 'string' ||
        (profile as any).avatar_url === null
      ) {
        profileAvatarUrl = (profile as any).avatar_url as string | null
      }
    }
  } catch {
    // fallback para metadata do auth
  }

  const rawName =
    (profileName && String(profileName).trim().length > 0 ? profileName : null) ??
    (meta.full_name as string | null | undefined) ??
    (meta.name as string | null | undefined) ??
    (user?.email as string | null | undefined) ??
    null

  const authorName = normalizeAuthorName(rawName)

  let authorAvatarUrl: string | null = null
  if (profileAvatarUrl !== undefined) {
    authorAvatarUrl = profileAvatarUrl
  } else if (typeof meta.avatar_url === 'string' || meta.avatar_url === null) {
    authorAvatarUrl = meta.avatar_url as string | null
  } else {
    authorAvatarUrl = null
  }

  const { data, error } = await supabase
    .from('market_visions' as any)
    .insert({
      author_id: userId,
      author_name: authorName,
      author_avatar_url: authorAvatarUrl,
      title,
      content_html: html,
      linked_opportunity_id: linkedOpportunityId,
      linked_timeline_root_id: effectiveTimelineRootId,
    } as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,linked_opportunity_id,linked_timeline_root_id,created_at')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('createVision error', error)
    throw error
  }

  const [item] = await mapRowsToVisionItems([data as any])
  return item ?? null
}

export async function listVisions(params: {
  page: number
  pageSize?: number
}): Promise<{ items: VisionItem[] }> {
  const page = Math.max(1, Number(params.page) || 1)
  const pageSize = Math.max(1, Number(params.pageSize) || 25)
  const { data, error } = await supabase.rpc('list_market_visions_feed' as any, {
    p_page: page,
    p_page_size: pageSize,
  })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listVisions error', error)
    throw error
  }

  const rows = Array.isArray(data) ? data : []
  const allowedRows = rows.filter((row: any) => Boolean(row?.can_view))
  const allowedItems = await mapRowsToVisionItems(allowedRows)
  const allowedById = new Map<number, VisionItem>()
  allowedItems.forEach((it) => {
    allowedById.set(Number(it.id), it)
  })

  const requiredPlanFromRow = (row: any): VisionRequiredPlan | null => {
    if (!row?.required_plan_id) return null
    return {
      id: String(row.required_plan_id || ''),
      name: String(row.required_plan_name || '').trim(),
      amount_cents:
        row.required_plan_amount_cents != null
          ? Number(row.required_plan_amount_cents)
          : null,
      currency: row.required_plan_currency ? String(row.required_plan_currency) : null,
      interval: row.required_plan_interval ? String(row.required_plan_interval) : null,
    }
  }

  const items = rows.map((row: any) => {
    const id = Number(row?.id)
    const canView = Boolean(row?.can_view)
    if (canView) {
      const base = allowedById.get(id)
      if (base) return base
    }
    return {
      id: Number.isFinite(id) ? id : 0,
      authorId: '',
      authorName: '',
      authorAvatarUrl: null,
      title: '',
      contentHtml: '',
      attachments: [],
      linkedOpportunityId: null,
      linkedTimelineRootId: normalizeLinkedTimelineRootId(row?.linked_timeline_root_id ?? null),
      createdAt: row?.created_at ? String(row.created_at) : '',
      canView: false,
      requiredPlan: requiredPlanFromRow(row),
      peopleCount: row?.people_count != null ? Number(row.people_count) : null,
    } as VisionItem
  })

  const allowedIds = allowedItems.map((it) => it.id)
  if (allowedIds.length > 0) {
    const attachmentsMap = await fetchVisionPostAttachmentsByVisionIds(allowedIds)
    for (const item of items) {
      if (!item.canView) continue
      item.attachments = attachmentsMap[String(item.id)] || []
    }
  }

  return { items }
}

export async function listVisionsByTimelineRootId(params: {
  timelineRootId: number
}): Promise<VisionItem[]> {
  const timelineRootId = normalizeLinkedTimelineRootId(params.timelineRootId)
  if (!timelineRootId) return []

  const { data, error } = await supabase
    .from('market_visions' as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,linked_opportunity_id,linked_timeline_root_id,created_at')
    .or(`id.eq.${timelineRootId},linked_timeline_root_id.eq.${timelineRootId}`)
    .order('created_at', { ascending: false })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listVisionsByTimelineRootId error', error)
    throw error
  }

  const items = await mapRowsToVisionItems(data as any)
  const ids = items.map((it) => it.id)
  if (ids.length > 0) {
    const attachmentsMap = await fetchVisionPostAttachmentsByVisionIds(ids)
    for (const item of items) {
      item.attachments = attachmentsMap[String(item.id)] || []
    }
  }

  return items
}

export async function listTimelineRootVisions(): Promise<VisionTimelineRootCandidate[]> {
  const { data, error } = await supabase
    .from('market_visions' as any)
    .select('id,title,content_html,author_name,author_avatar_url,created_at')
    .is('linked_opportunity_id', null)
    .is('linked_timeline_root_id', null)
    .order('created_at', { ascending: false })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listTimelineRootVisions error', error)
    throw error
  }

  return (Array.isArray(data) ? data : []).map((row: any) => ({
    id: Number(row.id),
    title: String(row.title || '').trim(),
    contentHtml: String(row.content_html || ''),
    authorName: normalizeAuthorName(row.author_name),
    authorAvatarUrl:
      typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
        ? (row.author_avatar_url as string | null)
        : null,
    createdAt: row.created_at ? String(row.created_at) : '',
  }))
}

export async function updateVision(
  id: number,
  params: {
    title: string
    html: string
    linkedOpportunityId?: number | null
    linkedTimelineRootId?: number | null
  },
): Promise<void> {
  const visionId = Number(id)
  if (!Number.isFinite(visionId) || visionId <= 0) return

  const title = String(params.title || '').trim()
  const html = String(params.html || '').trim()
  if (!title || !html) return

  const payload: any = {
    title,
    content_html: html,
  }

  if (params.linkedOpportunityId !== undefined) {
    payload.linked_opportunity_id = normalizeLinkedOpportunityId(params.linkedOpportunityId)
  }
  if (params.linkedTimelineRootId !== undefined) {
    payload.linked_timeline_root_id = normalizeLinkedTimelineRootId(params.linkedTimelineRootId)
  }

  const { error } = await supabase
    .from('market_visions' as any)
    .update(payload)
    .eq('id', visionId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('updateVision error', error)
    throw error
  }
}

export async function deleteVision(id: number): Promise<void> {
  const visionId = Number(id)
  if (!Number.isFinite(visionId) || visionId <= 0) return

  const { error } = await supabase
    .from('market_visions' as any)
    .delete()
    .eq('id', visionId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteVision error', error)
    throw error
  }
}

async function mapRowsToVisionItems(rowsRaw: any): Promise<VisionItem[]> {
  const rows = Array.isArray(rowsRaw) ? rowsRaw : []
  if (!rows.length) return []

  const authorIds = Array.from(
    new Set(
      rows
        .map((row: any) => (row.author_id ? String(row.author_id) : ''))
        .filter((id: string) => !!id),
    ),
  )

  const profilesMap: Record<
    string,
    {
      name: string
      avatarUrl: string | null
    }
  > = {}

  if (authorIds.length > 0) {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles' as any)
        .select('id,full_name,avatar_url')
        .in('id', authorIds as any)

      if (profilesError) {
        // eslint-disable-next-line no-console
        console.error('listVisions profiles error', profilesError)
      } else if (Array.isArray(profiles)) {
        profiles.forEach((p: any) => {
          const id = p.id ? String(p.id) : ''
          if (!id) return
          profilesMap[id] = {
            name: String(p.full_name || '').trim(),
            avatarUrl:
              typeof p.avatar_url === 'string' || p.avatar_url === null
                ? (p.avatar_url as string | null)
                : null,
          }
        })
      }
    } catch {
      // fallback para snapshots
    }
  }

  const items: VisionItem[] = rows.map((row: any) => {
    const authorId = row.author_id ? String(row.author_id) : ''
    const profileOverride = authorId ? profilesMap[authorId] : undefined

    const overrideName = profileOverride?.name?.trim()
    const rawName =
      (overrideName && overrideName.length > 0 ? overrideName : null) ??
      (typeof row.author_name === 'string' ? (row.author_name as string) : null)

    const authorName = normalizeAuthorName(rawName)

    let authorAvatarUrl: string | null = null
    if (profileOverride && profileOverride.avatarUrl !== undefined) {
      authorAvatarUrl = profileOverride.avatarUrl
    } else if (
      typeof row.author_avatar_url === 'string' ||
      row.author_avatar_url === null
    ) {
      authorAvatarUrl = row.author_avatar_url as string | null
    }

    return {
      id: Number(row.id),
      authorId,
      authorName,
      authorAvatarUrl,
      title: String(row.title || '').trim(),
      contentHtml: String(row.content_html || ''),
      attachments: [],
      linkedOpportunityId: normalizeLinkedOpportunityId((row as any).linked_opportunity_id),
      linkedTimelineRootId: normalizeLinkedTimelineRootId((row as any).linked_timeline_root_id),
      createdAt: row.created_at ? String(row.created_at) : '',
      canView: true,
      requiredPlan: null,
      peopleCount: null,
    }
  })

  return items
}
